import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';
import Button from '../../components/base/Button';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/thank-you#webpage`,
  url: `${siteUrl}/thank-you`,
  name: 'Thank You for Your Donation | Diatomic Life Foundation',
  description:
    'Your generous donation to Diatomic Life Foundation is making a real difference. Thank you for supporting oxygen supplementation and health education for families in need.',
  isPartOf: { '@id': `${siteUrl}/#website` },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Donate', item: `${siteUrl}/donate` },
      { '@type': 'ListItem', position: 3, name: 'Thank You', item: `${siteUrl}/thank-you` },
    ],
  },
  noIndex: true,
};

interface DonationSummary {
  name: string;
  email: string;
  amount: string;
  paymentId: string;
  isMonthly: string;
  date: string;
  receiptNumber: string;
}

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<DonationSummary | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    const name = searchParams.get('name') || '';
    const email = searchParams.get('email') || '';
    const amount = searchParams.get('amount') || '';
    const paymentId = searchParams.get('paymentId') || '';
    const isMonthly = searchParams.get('isMonthly') || 'false';
    const receiptNumber = searchParams.get('receiptNumber') || `DLF-${Date.now().toString().slice(-8)}`;
    const date = new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    if (name || amount || paymentId) {
      setSummary({ name, email, amount, paymentId, isMonthly, date, receiptNumber });
    }

    setShowConfetti(true);
    const timer = setTimeout(() => setShowConfetti(false), 3500);
    return () => clearTimeout(timer);
  }, [searchParams]);

  const formattedAmount = summary?.amount
    ? `₹${parseInt(summary.amount).toLocaleString('en-IN')}`
    : '₹0';

  const familiesSupported = summary?.amount
    ? Math.floor(parseInt(summary.amount) / 5000)
    : 0;

  const downloadReceipt = () => {
    const receiptWindow = window.open('', '_blank', 'width=800,height=900');
    if (!receiptWindow) return;

    const donorName = summary?.name || 'Valued Donor';
    const donorEmail = summary?.email || '—';
    const donationAmount = formattedAmount;
    const paymentId = summary?.paymentId || '—';
    const donationDate = summary?.date || new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    const paymentType = summary?.isMonthly === 'true' ? 'Monthly Recurring' : 'One-time Donation';
    const receiptNo = summary?.receiptNumber || `DLF-${Date.now().toString().slice(-8)}`;

    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Donation Receipt - Diatomic Life Foundation</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Inter', sans-serif; background: #f8f8f8; color: #111; padding: 40px 20px; }
          .receipt { max-width: 680px; margin: 0 auto; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 32px rgba(0,0,0,0.10); }
          .receipt-header { background: #12B6B6; padding: 36px 40px 28px; color: #fff; display: flex; align-items: center; justify-content: space-between; }
          .receipt-header .org-name { font-size: 20px; font-weight: 800; letter-spacing: -0.5px; }
          .receipt-header .org-tagline { font-size: 12px; opacity: 0.85; margin-top: 4px; }
          .receipt-header .badge { background: rgba(255,255,255,0.2); border-radius: 10px; padding: 8px 16px; font-size: 12px; font-weight: 600; text-align: center; }
          .receipt-header .badge .label { opacity: 0.8; font-size: 10px; display: block; margin-bottom: 2px; }
          .amount-block { background: #f0fafa; border-bottom: 1px solid #e0f5f5; padding: 28px 40px; display: flex; align-items: center; justify-content: space-between; }
          .amount-block .amount { font-size: 42px; font-weight: 800; color: #12B6B6; letter-spacing: -1px; }
          .amount-block .amount-label { font-size: 13px; color: #666; margin-top: 4px; }
          .amount-block .check-icon { width: 56px; height: 56px; background: #12B6B6; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
          .amount-block .check-icon svg { width: 28px; height: 28px; }
          .receipt-body { padding: 32px 40px; }
          .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.2px; color: #12B6B6; margin-bottom: 16px; }
          .detail-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 0; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
          .detail-row:last-child { border-bottom: none; }
          .detail-row .label { color: #888; }
          .detail-row .value { font-weight: 600; color: #111; text-align: right; max-width: 60%; word-break: break-all; }
          .detail-row .value.mono { font-family: monospace; font-size: 12px; background: #f5f5f5; padding: 3px 8px; border-radius: 6px; }
          .divider { height: 1px; background: #f0f0f0; margin: 24px 0; }
          .tax-note { background: #f0fafa; border: 1px solid #c8eeee; border-radius: 10px; padding: 16px 20px; font-size: 12.5px; color: #444; line-height: 1.6; margin-top: 24px; }
          .tax-note strong { color: #12B6B6; }
          .receipt-footer { background: #111; padding: 20px 40px; display: flex; align-items: center; justify-content: space-between; }
          .receipt-footer .footer-text { color: #aaa; font-size: 11px; }
          .receipt-footer .footer-brand { color: #fff; font-size: 13px; font-weight: 700; }
          .watermark { text-align: center; padding: 16px; font-size: 11px; color: #bbb; }
          @media print { body { background: #fff; padding: 0; } .receipt { box-shadow: none; border-radius: 0; } .no-print { display: none !important; } }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="receipt-header">
            <div>
              <div class="org-name">Diatomic Life Foundation</div>
              <div class="org-tagline">Oxygen Supplementation &amp; Health Education</div>
            </div>
            <div class="badge">
              <span class="label">Receipt No.</span>
              ${receiptNo}
            </div>
          </div>
          <div class="amount-block">
            <div>
              <div class="amount">${donationAmount}</div>
              <div class="amount-label">${paymentType}</div>
            </div>
            <div class="check-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          <div class="receipt-body">
            <div class="section-title">Donor Information</div>
            <div class="detail-row"><span class="label">Full Name</span><span class="value">${donorName}</span></div>
            <div class="detail-row"><span class="label">Email Address</span><span class="value">${donorEmail}</span></div>
            <div class="divider"></div>
            <div class="section-title">Payment Details</div>
            <div class="detail-row"><span class="label">Donation Amount</span><span class="value">${donationAmount}</span></div>
            <div class="detail-row"><span class="label">Payment Type</span><span class="value">${paymentType}</span></div>
            <div class="detail-row"><span class="label">Date</span><span class="value">${donationDate}</span></div>
            <div class="detail-row"><span class="label">Payment ID</span><span class="value mono">${paymentId}</span></div>
            <div class="detail-row"><span class="label">80G Tax Benefit</span><span class="value" style="color:#12B6B6;">Eligible ✓</span></div>
            <div class="tax-note">
              <strong>80G Tax Deduction Notice:</strong> This donation is eligible for tax deduction under Section 80G of the Income Tax Act, 1961. Please retain this receipt for your tax filing. An official 80G certificate will be sent to your registered email address.
            </div>
          </div>
          <div class="receipt-footer">
            <div class="footer-text">Thank you for your generous support</div>
            <div class="footer-brand">Diatomic Life Foundation</div>
          </div>
        </div>
        <div class="watermark">This is a computer-generated receipt and does not require a physical signature.</div>
        <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); };<\/script>
      </body>
      </html>
    `);
    receiptWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Thank You for Your Donation | Diatomic Life Foundation"
        description="Your generous donation to Diatomic Life Foundation is making a real difference. Thank you for supporting oxygen supplementation and health education for families in need."
        keywords="thank you donation, Diatomic Life Foundation, oxygen supplementation support"
        canonical={`${siteUrl}/thank-you`}
        schemaJson={schema}
      />
      <Header />

      {/* Confetti particles */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-40 overflow-hidden">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-bounce"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-${Math.random() * 20}px`,
                backgroundColor: ['#12B6B6', '#111111', '#f59e0b', '#10b981', '#f43f5e'][i % 5],
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1.5 + Math.random() * 2}s`,
                transform: `translateY(${Math.random() * 100}vh)`,
                transition: `transform ${2 + Math.random() * 2}s ease-in`,
              }}
            />
          ))}
        </div>
      )}

      <main className="pt-28 pb-20">
        {/* Hero Thank You */}
        <section className="max-w-4xl mx-auto px-6 text-center mb-16">
          <div className="w-24 h-24 flex items-center justify-center bg-[#12B6B6]/10 rounded-full mx-auto mb-8 animate-pulse">
            <i className="ri-heart-fill text-5xl text-[#12B6B6]"></i>
          </div>
          <h1 className="text-5xl font-bold text-black mb-4 leading-tight">
            Thank You{summary?.name ? `, ${summary.name.split(' ')[0]}` : ''}!
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Your generosity is breathing life into families who need it most. Every rupee you've given goes directly toward oxygen supplementation, health education, and AI-powered screening.
          </p>

          {/* Email confirmation notice */}
          {summary?.email && (
            <div className="mt-8 inline-flex items-center gap-3 bg-[#12B6B6]/8 border border-[#12B6B6]/25 rounded-2xl px-6 py-3.5">
              <div className="w-8 h-8 flex items-center justify-center bg-[#12B6B6] rounded-full shrink-0">
                <i className="ri-mail-check-line text-white text-sm"></i>
              </div>
              <p className="text-sm text-gray-700">
                A receipt has been sent to <strong className="text-black">{summary.email}</strong>
              </p>
            </div>
          )}
        </section>

        {/* Donation Summary Card */}
        <section className="max-w-2xl mx-auto px-6 mb-16">
          <div className="bg-white border border-gray-100 rounded-3xl shadow-lg overflow-hidden">
            {/* Card Header */}
            <div className="bg-[#12B6B6] px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/80 mb-1">Donation Confirmed</p>
                  <p className="text-3xl font-bold">{formattedAmount}</p>
                  {summary?.isMonthly === 'true' && (
                    <span className="inline-block mt-2 bg-white/20 text-white text-xs px-3 py-1 rounded-full">
                      Monthly Recurring
                    </span>
                  )}
                </div>
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl">
                  <i className="ri-checkbox-circle-fill text-4xl text-white"></i>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="px-8 py-6 space-y-4">
              {summary?.name && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-user-line text-[#12B6B6]"></i> Donor Name
                  </span>
                  <span className="text-sm font-semibold text-black">{summary.name}</span>
                </div>
              )}
              {summary?.email && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-mail-line text-[#12B6B6]"></i> Email
                  </span>
                  <span className="text-sm font-semibold text-black">{summary.email}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <i className="ri-calendar-line text-[#12B6B6]"></i> Date
                </span>
                <span className="text-sm font-semibold text-black">{summary?.date || new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <i className="ri-bank-card-line text-[#12B6B6]"></i> Payment Type
                </span>
                <span className="text-sm font-semibold text-black">
                  {summary?.isMonthly === 'true' ? 'Monthly Recurring' : 'One-time Donation'}
                </span>
              </div>
              {summary?.paymentId && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-receipt-line text-[#12B6B6]"></i> Payment ID
                  </span>
                  <span className="text-xs font-mono font-semibold text-black bg-gray-50 px-2 py-1 rounded-lg">{summary.paymentId}</span>
                </div>
              )}
              {summary?.receiptNumber && (
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-[#12B6B6]"></i> Receipt No.
                  </span>
                  <span className="text-xs font-mono font-semibold text-black bg-gray-50 px-2 py-1 rounded-lg">{summary.receiptNumber}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-3">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <i className="ri-file-text-line text-[#12B6B6]"></i> 80G Receipt
                </span>
                <span className="text-sm font-semibold text-[#12B6B6] flex items-center gap-1">
                  <i className="ri-mail-check-line"></i> Sent to your email
                </span>
              </div>
            </div>

            {/* Tax Note */}
            <div className="mx-8 mb-6 p-4 bg-[#12B6B6]/5 rounded-2xl border border-[#12B6B6]/20">
              <p className="text-xs text-gray-600 text-center leading-relaxed">
                <i className="ri-shield-check-line text-[#12B6B6] mr-1"></i>
                Your donation is eligible for <strong>80G tax deduction</strong>. An official receipt has been sent to your registered email address.
              </p>
            </div>

            {/* Download Receipt Button */}
            <div className="px-8 pb-8">
              <button
                onClick={downloadReceipt}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-black text-white rounded-2xl text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-download-2-line text-base"></i>
                Download Receipt (PDF)
              </button>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        {familiesSupported > 0 && (
          <section className="max-w-4xl mx-auto px-6 mb-16">
            <h2 className="text-2xl font-bold text-black text-center mb-8">Your Donation's Impact</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#12B6B6]/10 rounded-xl mx-auto mb-4">
                  <i className="ri-group-line text-2xl text-[#12B6B6]"></i>
                </div>
                <div className="text-3xl font-bold text-black mb-1">{familiesSupported}</div>
                <div className="text-sm text-gray-600">{familiesSupported === 1 ? 'Family' : 'Families'} Supported</div>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#12B6B6]/10 rounded-xl mx-auto mb-4">
                  <i className="ri-capsule-line text-2xl text-[#12B6B6]"></i>
                </div>
                <div className="text-3xl font-bold text-black mb-1">{familiesSupported * 12}</div>
                <div className="text-sm text-gray-600">Months of OxyKit Supply</div>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#12B6B6]/10 rounded-xl mx-auto mb-4">
                  <i className="ri-heart-pulse-line text-2xl text-[#12B6B6]"></i>
                </div>
                <div className="text-3xl font-bold text-black mb-1">{familiesSupported * 2}</div>
                <div className="text-sm text-gray-600">Lives Directly Impacted</div>
              </div>
            </div>
          </section>
        )}

        {/* Share Section */}
        <section className="max-w-2xl mx-auto px-6 mb-16 text-center">
          <h2 className="text-2xl font-bold text-black mb-3">Spread the Word</h2>
          <p className="text-gray-600 mb-6 text-sm">Help us reach more families by sharing your support.</p>
          <div className="flex items-center justify-center gap-4">
            <a
              href={`https://twitter.com/intent/tweet?text=I just donated to @DiatomicLife Foundation to support oxygen supplementation for families in need! Join me in making a difference. %23DiatomicLife %23OxygenForAll&url=${siteUrl}/donate`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-black text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-twitter-x-line"></i> Share on X
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${siteUrl}/donate`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-[#1877F2] text-white rounded-xl text-sm font-medium hover:bg-[#1565d8] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-facebook-fill"></i> Share on Facebook
            </a>
            <a
              href={`https://wa.me/?text=I just donated to Diatomic Life Foundation to support oxygen supplementation for families in need! Join me: ${siteUrl}/donate`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 bg-[#25D366] text-white rounded-xl text-sm font-medium hover:bg-[#1ebe5a] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line"></i> Share on WhatsApp
            </a>
          </div>
        </section>

        {/* CTA Buttons */}
        <section className="max-w-xl mx-auto px-6 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/donate">
              <Button variant="primary" size="lg">
                <i className="ri-heart-line mr-2"></i> Donate Again
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" size="lg">
                <i className="ri-home-line mr-2"></i> Back to Home
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
